/**
 * Quais blocos da ficha cada etiqueta destrava.
 *
 * ── 🔴 A regra, em uma frase: A ETIQUETA SOMA CAPACIDADE, NUNCA SUBTRAI.
 *
 * Um bloco declara UMA etiqueta que o destrava. Quem tem a etiqueta vê o bloco;
 * quem não tem, não vê. Quem tem DUAS etiquetas vê os dois conjuntos — porque a
 * pergunta é sempre "tem X?", nunca "não tem Y?".
 *
 * A diferença não é estilística. Um predicado por AUSÊNCIA ("mostra os campos de
 * funcionário só para quem NÃO é cliente") esconderia os campos justamente de
 * quem é as duas coisas — e na base do Omie isso não é hipótese: dos 12 cadastros
 * com `Funcionário`, 5 também têm `Cliente`. O predicado por ausência erraria em
 * 5 dos 12 casos, e erraria em silêncio.
 *
 * ── ⚠️ Este motor NASCEU nesta rodada — não existia antes
 *
 * O handoff pedia para reusar "o mesmo mecanismo do Portal do Cliente, dos
 * Contratos e dos Acessos e senhas, que só aparecem para quem tem `Cliente`".
 * Fui atrás dele e **ele não existe**: as abas Contratos e Portal do Cliente são
 * incondicionais (`ClientDetailPage.tsx`, lista fixa de abas), e a única aba que
 * some — Acessos e senhas — some por DEPARTAMENTO (`podeVerCofre`, manutenção e
 * projetos), não por etiqueta. Nenhum campo da ficha olha para a etiqueta hoje.
 *
 * O que existe de verdade é o irmão deste arquivo no servidor,
 * `server/lib/clientes/etiqueta-visibilidade.ts` — e ele faz o CONTRÁRIO: usa a
 * etiqueta para ESCONDER (fornecedor some do seletor, funcionário some de tudo
 * para quem não é admin). Por isso os dois vivem com o mesmo sobrenome e
 * comentários que se citam: são as duas metades de um vocabulário só, e quem
 * mexer num precisa saber do outro.
 *
 * ── ⚠️ Comparação sensível a maiúscula e acento, de propósito
 *
 * A trava do servidor compara `t.tag = 'Funcionário'` — exata, com acento. Se
 * aqui a comparação fosse tolerante, o front mostraria os campos de um cadastro
 * marcado `funcionario` que o servidor não considera funcionário: a ficha
 * ofereceria campos que a lista não esconde. Front e servidor discordando sobre
 * quem é funcionário é pior do que os dois serem estritos.
 *
 * O jeito certo de afrouxar isso é afrouxar OS DOIS ao mesmo tempo, e a trava do
 * servidor está no ar e medida (12 cadastros). Fica registrado, não feito.
 *
 * A ÚNICA diferença deliberada é o `trim()`: o servidor compara a coluna crua,
 * aqui os espaços em volta são ignorados. Na prática elas não divergem — a rota
 * que grava tag já faz `z.string().trim()`, e as 12 linhas do beta estão limpas
 * (medido em 11/08). Se um dia divergirem, o efeito é cosmético: o bloco aparece
 * num cadastro que o servidor não conta como funcionário. O salário não depende
 * disto — ele é `requireAdmin`, não etiqueta.
 */

/** A etiqueta que destrava o bloco de funcionário. Mesma string da trava do servidor. */
export const ETIQUETA_FUNCIONARIO = 'Funcionário';

/**
 * P-3b (18/08): Portal do Cliente, Contratos e Acessos e senhas são
 * EXCLUSIVOS de cadastro que TEM esta etiqueta — presença, nunca ausência
 * das outras (fornecedor que também é cliente vê tudo; 80 híbridos hoje).
 * O servidor barra junto (`requireEtiquetaCliente`).
 */
export const ETIQUETA_CLIENTE = 'Cliente';

/** As abas da ficha que a P-3b reserva a cadastro com etiqueta Cliente. */
export const ABAS_EXCLUSIVAS_DE_CLIENTE = ['contratos', 'portal-cliente', 'acessos'] as const;

/**
 * A etiqueta que destrava a ABA de fornecedor (mig 465).
 *
 * ⚠️ Aqui a assimetria com o funcionário importa e não é descuido: o servidor
 * usa `Fornecedor` para esconder o cadastro do SELETOR do Comercial
 * (`SQL_SEM_FORNECEDOR`), mas a ficha continua abrindo por id — ao contrário do
 * funcionário, que some de tudo para quem não é admin.
 *
 * Consequência prática: a aba de fornecedor aparece para quem abre a ficha, sem
 * exigir admin. É o certo — as regras de faturamento são operação de Compras,
 * não segredo. Salário é o caso oposto, e por isso tem `requireAdmin` próprio.
 */
export const ETIQUETA_FORNECEDOR = 'Fornecedor';

/**
 * O cadastro tem esta etiqueta?
 *
 * PRESENÇA — nunca ausência. Não existe (e não deve passar a existir) uma função
 * irmã `naoTemEtiqueta` usada para esconder bloco: esconder é assunto do
 * servidor, e lá é por admin, não por etiqueta.
 */
export function temEtiqueta(tags: readonly string[] | undefined | null, alvo: string): boolean {
  if (!tags || tags.length === 0) return false;
  return tags.some((t) => t.trim() === alvo);
}
